import React from 'react'

/**
 * AiReasoningReport
 *
 * Renders the industry feedback categories the AI detected for a feedback
 * (RTF-4809 / RTF-4898). Driven entirely by the `matches` prop, which is the
 * `industryCategoryMatches` array returned by FeedbackController::ajaxFeedback:
 *
 *   [{ id, reference_name, title, description, psychology,
 *      guidelines: string[], avoid: string[], variants: {signal, adjustment}[], tone }]
 *
 * Shows a "Feedback Insights" pill above the Chat section and, on click, a modal
 * with a single-open accordion of the detected categories and their per-category
 * guidance. Renders nothing when there are no matches.
 *
 * getfbapp.com uses Bootstrap 4 + SASS (no Tailwind); the violet design is
 * reproduced with inline styles plus a scoped <style> block for the keyframes.
 */

// reference_name -> Font Awesome Free 5.15.4 (solid) icon class suffix.
// There is no icon column in industry_categories, so this map is the single
// client-side source of truth for category icons. Repeats are intentional
// (e.g. every positive_* bucket uses fa-thumbs-up). Falls back to ICON_FALLBACK
// for any reference_name not listed (e.g. a future industry's categories).
const CATEGORY_ICONS = {
    'access_control': 'fa-key',
    'access_door_locks': 'fa-lock',
    'accessibility_ada_assistance': 'fa-wheelchair',
    'accessibility_and_special_needs': 'fa-wheelchair',
    'allergy_dietary': 'fa-allergies',
    'ambiance_environment': 'fa-lightbulb',
    'amenities_common_spaces': 'fa-couch',
    'amenity_downtime': 'fa-hot-tub',
    'amenity_requests': 'fa-concierge-bell',
    'animal_welfare_observations': 'fa-paw',
    'appointment_scheduling': 'fa-calendar-check',
    'automatic_gratuity': 'fa-percent',
    'av_screen_sharing': 'fa-desktop',
    'bathroom_maintenance': 'fa-toilet',
    'bathroom_safety': 'fa-bath',
    'behavior_conduct_concerns': 'fa-exclamation-circle',
    'beverage_cart_and_on_course_service': 'fa-glass-cheers',
    'billing': 'fa-file-invoice-dollar',
    'billing_account': 'fa-file-invoice-dollar',
    'billing_accuracy_and_coupons': 'fa-file-invoice-dollar',
    'billing_and_charges': 'fa-file-invoice-dollar',
    'billing_fees': 'fa-file-invoice-dollar',
    'billing_quotes_and_charges': 'fa-file-invoice-dollar',
    'billing_receipts_refunds': 'fa-receipt',
    'birthday_parties_and_group_events': 'fa-birthday-cake',
    'booking_refunds_and_scheduling': 'fa-calendar-check',
    'booking_scheduling_and_communication': 'fa-calendar-check',
    'bowling_lane_issues': 'fa-bowling-ball',
    'bug_and_debris_removal': 'fa-bug',
    'building_infrastructure': 'fa-building',
    'cart_path_restrictions_and_roping': 'fa-route',
    'cashier_attitude': 'fa-user-tie',
    'check_in_experience': 'fa-concierge-bell',
    'checkout_speed': 'fa-cash-register',
    'checkout_wait_understaffing': 'fa-cash-register',
    'class_quality': 'fa-running',
    'class_scheduling': 'fa-calendar-alt',
    'cleaning_janitorial': 'fa-broom',
    'cleanliness_and_vehicle_care': 'fa-spray-can',
    'cleanliness_floor': 'fa-hand-sparkles',
    'cleanliness_locker': 'fa-shower',
    'coffee_kitchen_supplies': 'fa-coffee',
    'common_area_cleanliness': 'fa-broom',
    'communication_and_expectations': 'fa-comments',
    'communication_and_tracking': 'fa-map-marked-alt',
    'communication_responsiveness': 'fa-comments',
    'communication_updates': 'fa-comments',
    'consultation_and_expectation_setting': 'fa-comment-dots',
    'course_conditions_fairways_and_rough': 'fa-leaf',
    'course_improvement_and_communication': 'fa-comments',
    'credits_taken_without_play': 'fa-coins',
    'crowding_and_capacity': 'fa-users',
    'customer_service_assistance': 'fa-concierge-bell',
    'deceptive_marketing_and_promotions': 'fa-bullhorn',
    'deli_food_quality': 'fa-drumstick-bite',
    'deli_hours': 'fa-clock',
    'deli_wait_times': 'fa-clock',
    'diagnostic_accuracy': 'fa-search',
    'disruptive_guest_behavior': 'fa-exclamation-triangle',
    'driver_professionalism_and_conduct': 'fa-user-tie',
    'drop_off_and_terminal_accuracy': 'fa-map-marker-alt',
    'drying_and_spotting': 'fa-tint',
    'elevator_reliability': 'fa-building',
    'equipment_out_of_order': 'fa-wrench',
    'equipment_selection': 'fa-clipboard-list',
    'etiquette_phone_hogging': 'fa-mobile-alt',
    'etiquette_weights': 'fa-weight-hanging',
    'event_information_schedules': 'fa-calendar-alt',
    'exhibits_and_interactive_features': 'fa-person-booth',
    'facility_access_and_layout': 'fa-map-signs',
    'facility_cleanliness': 'fa-broom',
    'facility_comfort_and_equipment': 'fa-couch',
    'facility_maintenance_equipment': 'fa-tools',
    'facility_maintenance_repairs': 'fa-tools',
    'facility_rental_reservations': 'fa-calendar-check',
    'filter_service_and_pricing': 'fa-filter',
    'floor_assistance_availability': 'fa-user-friends',
    'food_and_beverage': 'fa-utensils',
    'food_and_beverage_quality': 'fa-utensils',
    'food_beverage': 'fa-utensils',
    'food_beverage_and_clubhouse': 'fa-utensils',
    'food_beverage_feedback': 'fa-utensils',
    'food_quality': 'fa-utensils',
    'food_service_and_ordering': 'fa-concierge-bell',
    'food_temperature': 'fa-thermometer-half',
    'front_desk_and_customer_service': 'fa-concierge-bell',
    'game_malfunction_and_did_not_start': 'fa-gamepad',
    'golf_cart_equipment_and_technology': 'fa-car-side',
    'gratuity_and_checkout_experience': 'fa-credit-card',
    'greens_quality_and_pin_placement': 'fa-golf-ball',
    'guided_encounters_and_tours': 'fa-user-friends',
    'high_pressure_sales_and_upselling': 'fa-hand-holding-usd',
    'hours_and_availability': 'fa-clock',
    'hours_of_operation': 'fa-clock',
    'housekeeping': 'fa-concierge-bell',
    'hvac_comfort': 'fa-thermometer-half',
    'incomplete_or_unperformed_service': 'fa-tools',
    'injection_technique_pain_and_bruising': 'fa-syringe',
    'instore_service_execution': 'fa-concierge-bell',
    'karting_and_track_experience': 'fa-flag-checkered',
    'late_pickup_and_missed_connections': 'fa-hourglass-half',
    'license_plate_reader_and_member_lane': 'fa-camera',
    'lighting': 'fa-lightbulb',
    'loading_physical_assistance': 'fa-dolly',
    'loaner_rental_availability': 'fa-car-side',
    'lobby_hotel_amenities': 'fa-hotel',
    'lost_and_found': 'fa-search-location',
    'luggage_handling': 'fa-suitcase-rolling',
    'maintenance_incomplete': 'fa-tools',
    'maintenance_quality': 'fa-wrench',
    'manager_staff_conduct': 'fa-user-tie',
    'meat_seafood_quality': 'fa-fish',
    'membership_cancellation_and_billing': 'fa-file-invoice-dollar',
    'menu_availability': 'fa-book-open',
    'military_veteran_discount': 'fa-medal',
    'misdirected_unrelated_inquiries': 'fa-question-circle',
    'missed_areas_and_coverage': 'fa-search',
    'move_in_condition': 'fa-box-open',
    'new_vehicle_delivery': 'fa-key',
    'no_balls_pucks_or_game_pieces': 'fa-futbol',
    'noise_disturbance': 'fa-volume-up',
    'noise_level': 'fa-volume-up',
    'noise_sound_privacy': 'fa-volume-up',
    'oil_change_errors_and_leaks': 'fa-oil-can',
    'oil_life_reset_and_stickers': 'fa-sticky-note',
    'onboard_environment_and_entertainment': 'fa-headphones',
    'onboarding': 'fa-door-open',
    'order_accuracy': 'fa-clipboard-check',
    'out_of_stock_inventory': 'fa-boxes',
    'overcrowding': 'fa-users',
    'pace_of_play': 'fa-stopwatch',
    'package_delivery': 'fa-box',
    'park_amenities_infrastructure': 'fa-tree',
    'park_safety_security': 'fa-shield-alt',
    'parking': 'fa-parking',
    'parking_lot': 'fa-parking',
    'parking_transportation': 'fa-parking',
    'parts_repair_delays': 'fa-cogs',
    'passenger_safety_and_child_restraints': 'fa-child',
    'payment_fees': 'fa-credit-card',
    'pest_control': 'fa-bug',
    'pets_cleanliness': 'fa-paw',
    'pool_amenities': 'fa-swimmer',
    'pool_aquatic_facilities': 'fa-swimmer',
    'pool_aquatics': 'fa-swimmer',
    'positive_feedback': 'fa-thumbs-up',
    'positive_general_feedback': 'fa-thumbs-up',
    'positive_staff_praise': 'fa-thumbs-up',
    'post_care_and_instructions': 'fa-notes-medical',
    'premium_suite_club_service': 'fa-glass-cheers',
    'price_mismatch_shelf_register': 'fa-tags',
    'pricing_and_value': 'fa-tag',
    'pricing_fees_and_promotions': 'fa-tag',
    'pricing_transparency': 'fa-file-invoice-dollar',
    'pricing_value': 'fa-tag',
    'pricing_vs_competitors': 'fa-balance-scale',
    'printing_issues': 'fa-print',
    'product_availability': 'fa-boxes',
    'product_quality': 'fa-box-open',
    'program_class_quality': 'fa-running',
    'recall_service': 'fa-exclamation-triangle',
    'redemption_claw_and_prize_machines': 'fa-gift',
    'refund_and_credit_requests': 'fa-money-bill-wave',
    'registration_information': 'fa-clipboard-list',
    'rent_value_lease': 'fa-file-contract',
    'repair_quality': 'fa-tools',
    'reservation_seating': 'fa-chair',
    'restroom_and_facility_maintenance': 'fa-toilet',
    'restroom_cleanliness': 'fa-toilet',
    'restroom_cleanliness_and_facilities': 'fa-toilet',
    'restrooms_and_on_course_amenities': 'fa-toilet',
    'returns_exchanges': 'fa-undo',
    'rewards_program_friction': 'fa-gift',
    'room_amenities': 'fa-mug-hot',
    'room_cleanliness': 'fa-hand-sparkles',
    'room_comfort_quality': 'fa-bed',
    'room_maintenance': 'fa-wrench',
    'safety_and_clinical_judgment': 'fa-user-md',
    'safety_concerns': 'fa-shield-alt',
    'safety_conduct': 'fa-shield-alt',
    'safety_incidents': 'fa-first-aid',
    'safety_medical_security_emergencies': 'fa-first-aid',
    'safety_security': 'fa-shield-alt',
    'sales_process_finance': 'fa-handshake',
    'server_conduct': 'fa-user-tie',
    'service_advisor_attitude': 'fa-user-tie',
    'service_speed_attentiveness': 'fa-concierge-bell',
    'service_tier_and_express_value': 'fa-star',
    'service_wait_times': 'fa-clock',
    'show_and_experience_reservations': 'fa-calendar-check',
    'special_events_festivals': 'fa-calendar-day',
    'special_occasions': 'fa-birthday-cake',
    'spill_and_cleaning_requests': 'fa-broom',
    'staff_attitude': 'fa-user-tie',
    'staff_conduct': 'fa-user-tie',
    'staff_conduct_and_professionalism': 'fa-user-tie',
    'staff_conduct_and_service': 'fa-user-tie',
    'staff_friendliness_engagement': 'fa-smile',
    'staff_instructor_praise': 'fa-thumbs-up',
    'staff_product_knowledge': 'fa-user-graduate',
    'staffing_and_assistance': 'fa-user-tie',
    'starter_and_ranger_conduct': 'fa-user-tie',
    'state_inspection_and_emissions': 'fa-clipboard-check',
    'store_cleanliness': 'fa-broom',
    'technician_errors': 'fa-user-cog',
    'tee_boxes_and_bunkers': 'fa-flag',
    'tee_times_and_booking': 'fa-calendar-check',
    'temperature_hvac': 'fa-thermometer-half',
    'ticketing_account_access': 'fa-ticket-alt',
    'ticketing_and_online_booking': 'fa-ticket-alt',
    'tickets_and_prize_disputes': 'fa-ticket-alt',
    'tire_rotation_and_pressure': 'fa-truck-monster',
    'tire_shine_and_add_ons': 'fa-air-freshener',
    'treatment_results_and_outcomes': 'fa-spa',
    'trip_duration_and_routing': 'fa-route',
    'unsafe_driving_and_safety_concerns': 'fa-exclamation-triangle',
    'upselling_and_hard_sell_tactics': 'fa-hand-holding-usd',
    'vacuums_and_cleaning_supplies': 'fa-box-open',
    'valet_parking': 'fa-suitcase-rolling',
    'value_pricing': 'fa-tag',
    'vehicle_cleanliness_and_condition': 'fa-broom',
    'vehicle_comfort_and_capacity': 'fa-shuttle-van',
    'vehicle_damage': 'fa-car-crash',
    'vehicle_damage_during_service': 'fa-car-crash',
    'vehicle_wash_detailing': 'fa-spray-can',
    'ventilation_temperature': 'fa-thermometer-half',
    'venue_entry_bag_policy': 'fa-door-open',
    'vr_and_simulator_experience': 'fa-vr-cardboard',
    'wait_times_and_appointment_delays': 'fa-clock',
    'wait_times_and_lines': 'fa-clock',
    'wait_times_and_speed': 'fa-clock',
    'wait_times_at_pickup': 'fa-clock',
    'waiting_facility': 'fa-couch',
    'warranty_disputes': 'fa-file-contract',
    'wash_quality_soap_and_rinse': 'fa-spray-can',
    'wayfinding_and_signage': 'fa-map-signs',
    'wifi_internet': 'fa-wifi',
    'youth_camp_experience': 'fa-campground',
}

const ICON_FALLBACK = 'fa-tag'

// Decorative palette cycled per detected category (color only affects the icon tint).
const PALETTE = ['#d97706', '#059669', '#2563eb', '#7c3aed', '#db2777', '#0891b2', '#ca8a04']

class AiReasoningReport extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            isOpen: false,
            openItem: 0,
            openSub: null,
            pressed: false,
            hovered: false,
        }
    }

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown)
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown)
    }

    handleKeyDown = (e) => {
        if (this.state.isOpen && (e.key === 'Escape' || e.key === 'Esc')) {
            this.closeModal()
        }
    }

    openModal = () => this.setState({ isOpen: true })
    closeModal = () => this.setState({ isOpen: false })

    toggleItem = (index) => {
        this.setState((prev) => ({ openItem: prev.openItem === index ? -1 : index }))
    }

    toggleSub = (catIndex, subIndex) => {
        const key = catIndex + ':' + subIndex
        this.setState((prev) => ({ openSub: prev.openSub === key ? null : key }))
    }

    // Map the backend `matches` payload into the render model used below.
    buildInsights() {
        const matches = Array.isArray(this.props.matches) ? this.props.matches : []

        return matches.map((m, i) => ({
            title: m.title || m.reference_name || 'Category',
            description: m.description || '',
            icon: CATEGORY_ICONS[m.reference_name] || ICON_FALLBACK,
            color: PALETTE[i % PALETTE.length],
            psychology: m.psychology || '',
            guidelines: Array.isArray(m.guidelines) ? m.guidelines : [],
            avoid: Array.isArray(m.avoid) ? m.avoid : [],
            variants: Array.isArray(m.variants) ? m.variants : [],
            tone: m.tone || '',
        }))
    }

    render() {
        const insights = this.buildInsights()

        // No detected categories -> render nothing (no pill, no modal).
        if (insights.length === 0) {
            return null
        }

        const count = insights.length
        const countLabel = count + ' ' + (count === 1 ? 'category' : 'categories')

        // Industry name for the CTA — same across all matches of a feedback.
        const industry = (Array.isArray(this.props.matches) && this.props.matches[0] && this.props.matches[0].industry) || ''
        const ctaPrefix = 'View Our Exclusive Best Practices Guidebook'
        const ctaSuffix = industry ? 'for the ' + industry + ' Industry' : ''

        // Guidebook PDF URL from the ajaxFeedback payload (industryAiGuideBook.url).
        const guideBookUrl = (this.props.guideBook && this.props.guideBook.url) || ''
        const guideBookTitle = (this.props.guideBook && this.props.guideBook.title) || ''

        // ?dl= is read by an AWS edge function that sets the PDF's Content-Disposition filename.
        const guideBookHref = (() => {
            if (!guideBookUrl) { return '' }
            if (!guideBookTitle) { return guideBookUrl }
            const sep = guideBookUrl.indexOf('?') === -1 ? '?' : '&'
            return guideBookUrl + sep + 'dl=' + encodeURIComponent(guideBookTitle + '.pdf')
        })()

        // Wrap the PDF in a tab whose <title> is the guidebook title so the browser tab shows it.
        const escapeHtml = (s) => String(s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
        const openGuideBook = (e) => {
            if (!guideBookUrl) { e.preventDefault(); return }
            const tab = window.open('', '_blank')
            if (!tab) { return } // popup blocked → let the <a target="_blank"> default fire
            e.preventDefault()
            const title = escapeHtml(guideBookTitle || 'Guidebook')
            const src = escapeHtml(guideBookHref)
            tab.document.write('<!doctype html><html><head><meta charset="utf-8"><title>' + title + '</title><style>html,body{margin:0;height:100%}iframe{display:block;border:0;width:100%;height:100%}</style></head><body><iframe src="' + src + '"></iframe></body></html>')
            tab.document.close()
        }

        // Only surface the per-category panels that actually have content, so a
        // category missing some modal_* fields doesn't render empty accordion rows.
        const subSections = (item) => {
            const out = []
            if (item.psychology) {
                out.push({ key: 'psychology', label: 'Psychology', icon: 'fa-brain',
                    body: <p style={{ margin: 0 }}>{item.psychology}</p> })
            }
            if (item.guidelines.length) {
                out.push({ key: 'guidelines', label: 'Best-practice guidelines', icon: 'fa-clipboard-check',
                    body: (
                        <ul style={{ margin: 0, paddingLeft: '18px' }}>
                            {item.guidelines.map((g, i) => <li key={i} style={{ marginBottom: '3px' }}>{g}</li>)}
                        </ul>
                    ) })
            }
            if (item.avoid.length) {
                out.push({ key: 'avoid', label: 'What not to say', icon: 'fa-ban',
                    body: (
                        <ul style={{ margin: 0, paddingLeft: '18px' }}>
                            {item.avoid.map((a, i) => <li key={i} style={{ marginBottom: '3px' }}>{a}</li>)}
                        </ul>
                    ) })
            }
            if (item.variants.length) {
                out.push({ key: 'variants', label: 'Signals & adjustments', icon: 'fa-table',
                    body: (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 700, width: '45%' }}>Signal</th>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 700 }}>Adjustment</th>
                                </tr>
                            </thead>
                            <tbody>
                                {item.variants.map((v, i) => (
                                    <tr key={i}>
                                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' }}>{v.signal}</td>
                                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' }}>{v.adjustment}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) })
            }
            if (item.tone) {
                out.push({ key: 'tone', label: 'Tone reminder', icon: 'fa-comment-dots',
                    body: <p style={{ margin: 0 }}>{item.tone}</p> })
            }
            return out
        }

        return (
            <div style={{ width: '100%' }}>
                <style>{`
                    @keyframes airr-pulse {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.12); opacity: 0.78; }
                    }
                    @keyframes airr-fade-in { from { opacity: 0; } to { opacity: 1; } }
                    @keyframes airr-pop-in {
                        from { opacity: 0; transform: translateY(8px) scale(0.98); }
                        to { opacity: 1; transform: translateY(0) scale(1); }
                    }
                `}</style>

                {/* Trigger pill */}
                <div style={{ width: '100%' }}>
                    <button
                        type="button"
                        onClick={this.openModal}
                        onMouseEnter={() => this.setState({ hovered: true })}
                        onMouseLeave={() => this.setState({ hovered: false, pressed: false })}
                        onMouseDown={() => this.setState({ pressed: true })}
                        onMouseUp={() => this.setState({ pressed: false })}
                        style={{
                            display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '10px',
                            width: '100%', boxSizing: 'border-box', padding: '10px 16px',
                            border: '2px solid #7eb4dd', borderRadius: '10px',
                            background: this.state.hovered ? '#dcebf8' : '#eef5fc', cursor: 'pointer',
                            transition: 'background 0.15s ease, transform 0.1s ease',
                            transform: this.state.pressed ? 'scale(0.99)' : 'scale(1)', outline: 'none',
                        }}
                    >
                        <svg width="12" height="15.6" viewBox="0 0 20 26" fill="none" xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true" style={{ flexShrink: 0, transformOrigin: 'center', animation: 'airr-pulse 1.6s ease-in-out 4' }}>
                            <path d="M11.125 10.333V1L1 15.667h7.875V25L19 10.333h-7.875Z" fill="#3775B7" stroke="#3775B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', flexShrink: 1, minWidth: 0 }}>
                            <span style={{ color: '#1C75BC', fontWeight: 700, fontSize: '14px', textAlign: 'left' }}>Feedback Insights</span>
                            <span style={{ color: '#374151', fontSize: '13px', textAlign: 'left' }}>Explore AI category analysis</span>
                        </span>
                        <span style={{ marginLeft: 'auto', flexShrink: 0, background: '#1C75BC', color: '#ffffff', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '9999px', lineHeight: '1.4' }}>{countLabel}</span>
                    </button>
                </div>

                {/* Modal */}
                {this.state.isOpen && (
                    <div onClick={this.closeModal} style={{ position: 'fixed', inset: 0, zIndex: 1080, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'rgba(17, 24, 39, 0.6)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', animation: 'airr-fade-in 0.15s ease' }}>
                        <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="AI Reasoning Report" style={{ width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', background: '#ffffff', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)', animation: 'airr-pop-in 0.18s ease' }}>
                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '20px 20px 16px', borderBottom: '1px solid #f3f4f6' }}>
                                <div style={{ flexShrink: 0, width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fas fa-magic" style={{ color: '#1C75BC', fontSize: '18px' }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <h5 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' }}>Feedback Insights</h5>
                                    <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6b7280' }}>{count} feedback {count === 1 ? 'category' : 'categories'} detected in this feedback</p>
                                </div>
                                <button type="button" onClick={this.closeModal} aria-label="Close" style={{ flexShrink: 0, border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '4px' }}>
                                    <i className="fas fa-times" />
                                </button>
                            </div>

                            {/* Accordion */}
                            <div style={{ padding: '16px 20px' }}>
                                {insights.map((item, index) => {
                                    const isItemOpen = this.state.openItem === index
                                    const subs = subSections(item)
                                    return (
                                        <div key={index} style={{ border: isItemOpen ? '1px solid #c4b5fd' : '1px solid #e5e7eb', background: isItemOpen ? '#f5f3ff' : '#ffffff', borderRadius: '12px', marginBottom: index === insights.length - 1 ? 0 : '10px', transition: 'background 0.15s ease, border-color 0.15s ease', overflow: 'hidden' }}>
                                            <button type="button" onClick={() => this.toggleItem(index)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', outline: 'none' }}>
                                                <span style={{ flexShrink: 0, width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <i className={'fas ' + item.icon} style={{ color: item.color, fontSize: '16px' }} />
                                                </span>
                                                <span style={{ flex: 1, fontSize: '14px', fontWeight: 600, color: '#111827' }}>{item.title}</span>
                                                <i className="fas fa-chevron-down" style={{ flexShrink: 0, color: '#9ca3af', fontSize: '12px', transition: 'transform 0.2s ease', transform: isItemOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                                            </button>
                                            {isItemOpen && (
                                                <div style={{ padding: '0 14px 14px 14px' }}>
                                                    {item.description && (
                                                        <p style={{ margin: '0 0 12px', paddingLeft: '44px', fontSize: '13px', lineHeight: '1.5', color: '#6b7280' }}>{item.description}</p>
                                                    )}
                                                    {subs.map((sub, si) => {
                                                        const subKey = index + ':' + si
                                                        const subOpen = this.state.openSub === subKey
                                                        return (
                                                            <div key={sub.key} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', background: '#ffffff', marginBottom: si === subs.length - 1 ? 0 : '8px', overflow: 'hidden' }}>
                                                                <button type="button" onClick={() => this.toggleSub(index, si)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', outline: 'none' }}>
                                                                    <i className={'fas ' + sub.icon} style={{ color: item.color, fontSize: '12px', width: '14px', textAlign: 'center', flexShrink: 0 }} />
                                                                    <span style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: '#374151' }}>{sub.label}</span>
                                                                    <i className="fas fa-chevron-down" style={{ flexShrink: 0, color: '#9ca3af', fontSize: '11px', transition: 'transform 0.2s ease', transform: subOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                                                                </button>
                                                                {subOpen && (
                                                                    <div style={{ padding: '0 12px 12px 36px', fontSize: '13px', lineHeight: '1.55', color: '#4b5563' }}>{sub.body}</div>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>

                            {/* CTA */}
                            <div style={{ padding: '4px 20px 20px' }}>
                                <a href={guideBookHref || '#'} onClick={openGuideBook} target={guideBookUrl ? '_blank' : undefined} rel={guideBookUrl ? 'noopener noreferrer' : undefined} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '12px 16px', borderRadius: '12px', background: 'linear-gradient(to right, #2b8fd6, #1C75BC)', color: '#ffffff', fontSize: '14px', fontWeight: 600, textDecoration: 'none', textAlign: 'center' }}>
                                    <i className="fas fa-external-link-alt" style={{ fontSize: '13px', flexShrink: 0 }} />
                                    <span style={{ textAlign: 'center' }}>
                                        {ctaPrefix}
                                        {ctaSuffix && (
                                            <>
                                                <br />
                                                {ctaSuffix}
                                            </>
                                        )}
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }
}

export default AiReasoningReport
